import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import clinicaService from '../../services/clinicaService';
import usuarioService from '../../services/usuarioService';
import api from '../../services/api';
import {
  Box, Typography, Paper, TextField, Button, Divider,
  Grid, Alert, Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Switch, FormControlLabel,
} from '@mui/material';
import {
  Person as PersonIcon,
  Image as ImageIcon,
  MedicalServices as AnamneseIcon,
  LocalHospital as ClinicaIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  Face as FaceIcon,
} from '@mui/icons-material';

// Modal de confirmação
function ModalConfirmar({ open, onClose, onConfirm, texto }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', border: '1px solid rgba(239,68,68,0.2)' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ef4444', fontWeight: 700 }}>
        <WarningIcon /> Confirmar Exclusão
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2">{texto || 'Tem certeza que deseja excluir?'}</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancelar</Button>
        <Button variant="contained" onClick={onConfirm} sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, borderRadius: '8px' }}>
          Excluir
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const Configuracao = () => {
  const { user, setUser } = useAuth();
  const { isDarkMode } = useAppTheme();

  const [nome, setNome] = useState(user?.nome || '');
  const [email, setEmail] = useState(user?.email || '');
  const [cro, setCro] = useState(user?.cro || '');
  const [senha, setSenha] = useState('');

  const [clinicaNome, setClinicaNome] = useState('');
  const [clinicaEmail, setClinicaEmail] = useState('');
  const [clinicaTelefone, setClinicaTelefone] = useState('');
  const [clinicaCnpj, setClinicaCnpj] = useState('');
  const [clinicaTipoPessoa, setClinicaTipoPessoa] = useState('');
  const [atendeOdontologia, setAtendeOdontologia] = useState(true);
  const [atendeEstetica, setAtendeEstetica] = useState(false);
  const [clinicaLoading, setClinicaLoading] = useState(true);
  const [clinicaErro, setClinicaErro] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [logoUrl, setLogoUrl] = useState(localStorage.getItem('logoUrl') || '');
  const [assinaturaUrl, setAssinaturaUrl] = useState(localStorage.getItem('assinaturaUrl') || '');

  const [anamneses, setAnamneses] = useState([]);
  const [loadingAnamneses, setLoadingAnamneses] = useState(true);
  const [erroAnamneses, setErroAnamneses] = useState('');
  const [modalAberto, setModalAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [anamneseEditando, setAnamneseEditando] = useState(null);
  const [anamneseExcluir, setAnamneseExcluir] = useState(null);
  const [editTitulo, setEditTitulo] = useState('');
  const [editConteudo, setEditConteudo] = useState('');

  const carregarAnamneses = async () => {
    setLoadingAnamneses(true);
    setErroAnamneses('');
    try {
      const data = await api.get('/termos');
      const lista = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setAnamneses(lista.filter(t => t.tipo === 'anamnese'));
    } catch {
      setErroAnamneses('Erro ao buscar modelos de anamnese');
    } finally {
      setLoadingAnamneses(false);
    }
  };

  useEffect(() => { carregarAnamneses(); }, []);

  useEffect(() => {
    const fetchClinica = async () => {
      const clinicaId = user?.clinicaId;
      if (!clinicaId) return;
      setClinicaLoading(true);
      try {
        const clinica = await clinicaService.buscarClinica(clinicaId);
        setClinicaNome(clinica.nome || clinica.nome_fantasia || '');
        setClinicaEmail(clinica.email || '');
        setClinicaTelefone(clinica.telefone || clinica.whatsapp || '');
        setClinicaCnpj(clinica.cnpj || '');
        setClinicaTipoPessoa(clinica.tipoPessoa || clinica.tipo_pessoa || '');
        setAtendeOdontologia(clinica.atendeOdontologia ?? clinica.atende_odontologia ?? true);
        setAtendeEstetica(clinica.atendeEstetica ?? clinica.atende_estetica ?? false);
        if (clinica.logo) setLogoUrl(clinica.logo);
        if (clinica.assinatura) setAssinaturaUrl(clinica.assinatura);
      } catch {
        setClinicaErro('Erro ao buscar dados da clínica');
      } finally {
        setClinicaLoading(false);
      }
    };
    fetchClinica();
  }, [user?.clinicaId]);

  const handleToggleOdontologia = (checked) => {
    if (!checked && !atendeEstetica) { setAtendeEstetica(true); }
    setAtendeOdontologia(checked);
  };

  const handleToggleEstetica = (checked) => {
    if (!checked && !atendeOdontologia) { setAtendeOdontologia(true); }
    setAtendeEstetica(checked);
  };

  const handleUpload = (e, setUrl, key) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => { setUrl(ev.target.result); localStorage.setItem(key, ev.target.result); };
    reader.readAsDataURL(file);
  };

  const handleSalvarTudo = async () => {
    setClinicaErro('');
    setSuccessMsg('');
    if (!clinicaEmail?.trim()) { setClinicaErro('O e-mail comercial da clínica é obrigatório.'); return; }
    setClinicaLoading(true);
    let erroUsuario = '', erroClinica = '';
    try {
      await usuarioService.atualizar(user.id, { nome, cro, senha: senha || undefined });
      const atualizado = await api.get(`/usuarios/${user.id}`);
      if (atualizado?.nome) {
        setNome(atualizado.nome); setCro(atualizado.cro || '');
        setEmail(atualizado.email || ''); setSenha('');
        setUser(atualizado); localStorage.setItem('user', JSON.stringify(atualizado));
      }
    } catch (err) { erroUsuario = `Erro ao salvar usuário: ${err.message || ''}`; }
    try {
      await clinicaService.atualizarClinica(user.clinicaId, {
        nome: clinicaNome, email: clinicaEmail, telefone: clinicaTelefone,
        cnpj: clinicaCnpj, tipoPessoa: clinicaTipoPessoa, logo: logoUrl, assinatura: assinaturaUrl,
        atendeOdontologia, atendeEstetica,
      });
    } catch (err) { erroClinica = `Erro ao salvar clínica: ${err.message || ''}`; }
    setClinicaLoading(false);
    if (!erroUsuario && !erroClinica) { setSuccessMsg('Todas as alterações foram salvas com sucesso!'); setTimeout(() => setSuccessMsg(''), 4000); }
    else setClinicaErro(`${erroUsuario} ${erroClinica}`.trim());
  };

  const abrirModalAnamnese = (anamnese = null) => {
    setAnamneseEditando(anamnese);
    setEditTitulo(anamnese?.titulo || '');
    setEditConteudo(anamnese?.conteudo || '');
    setModalAberto(true);
  };

  const handleSalvarAnamnese = async () => {
    if (!editTitulo.trim()) return;
    setSalvando(true);
    try {
      if (anamneseEditando) {
        await api.put(`/termos/${anamneseEditando.id}`, { titulo: editTitulo, conteudo: editConteudo });
      } else {
        await api.post('/termos', { titulo: editTitulo, tipo: 'anamnese', conteudo: editConteudo });
      }
      setModalAberto(false); setAnamneseEditando(null);
      await carregarAnamneses();
    } catch { alert('Erro ao salvar modelo de anamnese'); }
    finally { setSalvando(false); }
  };

  const handleExcluirAnamnese = async () => {
    if (!anamneseExcluir) return;
    try {
      await api.delete(`/termos/${anamneseExcluir.id}`);
      setAnamneseExcluir(null);
      await carregarAnamneses();
    } catch { alert('Erro ao excluir modelo de anamnese'); }
  };

  // Estilos reutilizáveis
  const cardSx = {
    p: 3, mb: 3, borderRadius: '12px',
    background: isDarkMode ? undefined : '#fff',
    borderTop: '3px solid #F97316',
    boxShadow: '0 2px 12px rgba(249,115,22,0.07)',
    border: '1px solid rgba(249,115,22,0.15)',
    borderTopWidth: '3px',
    borderTopColor: '#F97316',
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      background: isDarkMode ? undefined : '#FAFAFA',
      '&:hover fieldset': { borderColor: 'rgba(249,115,22,0.5)' },
      '&.Mui-focused fieldset': { borderColor: '#F97316' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#F97316' },
  };

  const sectionTitle = (Icon, label) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, pb: 1.5, borderBottom: '1px solid rgba(249,115,22,0.12)' }}>
      <Icon sx={{ color: '#F97316', fontSize: '1.2rem' }} />
      <Typography variant="subtitle1" fontWeight={700} sx={{ color: isDarkMode ? '#fff' : '#1C1917' }}>
        {label}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 4, backgroundColor: isDarkMode ? undefined : '#FFFAF5', minHeight: '100vh' }}>
      {/* Título */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ borderLeft: '4px solid #F97316', pl: 1.5 }}>
          Configurações Gerais
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, pl: 1.5 }}>
          Ajuste seu perfil pessoal, a identidade da clínica e questionários.
        </Typography>
      </Box>

      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}
      {clinicaErro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setClinicaErro('')}>{clinicaErro}</Alert>}

      {/* Dados do Usuário */}
      <Paper elevation={0} sx={cardSx}>
        {sectionTitle(PersonIcon, 'Meus Dados de Usuário')}
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Nome Completo" value={nome} onChange={e => setNome(e.target.value)} sx={fieldSx} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="E-mail de Acesso (Login)" value={email} disabled sx={fieldSx} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="CRO / Registro Profissional" value={cro} onChange={e => setCro(e.target.value)} sx={fieldSx} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Alterar Senha" type="password" value={senha} onChange={e => setSenha(e.target.value)} placeholder="Digite uma nova senha se desejar mudar" autoComplete="new-password" sx={fieldSx} />
          </Grid>
        </Grid>
      </Paper>

      {/* Identidade Visual */}
      <Paper elevation={0} sx={cardSx}>
        {sectionTitle(ImageIcon, 'Identidade Visual dos Documentos')}
        <Grid container spacing={3}>
          {/* Logo */}
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ color: '#F97316', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 1 }}>Logotipo da Clínica</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ width: 100, height: 100, borderRadius: '10px', border: '2px dashed rgba(249,115,22,0.3)', bgcolor: 'rgba(249,115,22,0.04)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexShrink: 0 }}>
                {logoUrl ? <img src={logoUrl} alt="Logo" style={{ maxHeight: 80, maxWidth: 88, objectFit: 'contain', borderRadius: 6 }} /> : <><ImageIcon sx={{ color: 'rgba(249,115,22,0.4)', fontSize: '2rem' }} /><Typography variant="caption" color="text.disabled">Logo PNG</Typography></>}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Esta logo aparecerá no topo de receitas, atestados e anamneses.</Typography>
                <input type="file" accept="image/png,image/jpeg" style={{ display: 'none' }} id="logo-upload" onChange={e => handleUpload(e, setLogoUrl, 'logoUrl')} />
                <label htmlFor="logo-upload"><Button component="span" variant="outlined" size="small" sx={{ borderColor: '#F97316', color: '#F97316', borderRadius: '8px', mr: 1 }}>Selecionar</Button></label>
                {logoUrl && <Button size="small" color="error" onClick={() => { setLogoUrl(''); localStorage.removeItem('logoUrl'); }}>Remover</Button>}
              </Box>
            </Box>
          </Grid>
          {/* Assinatura */}
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ color: '#F97316', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 1 }}>Assinatura Digitalizada</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ width: 160, height: 70, borderRadius: '10px', border: '2px dashed rgba(249,115,22,0.3)', bgcolor: 'rgba(249,115,22,0.04)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexShrink: 0 }}>
                {assinaturaUrl ? <img src={assinaturaUrl} alt="Assinatura" style={{ maxHeight: 60, maxWidth: 148, objectFit: 'contain', borderRadius: 4 }} /> : <><ImageIcon sx={{ color: 'rgba(249,115,22,0.4)', fontSize: '1.5rem' }} /><Typography variant="caption" color="text.disabled">Assinatura</Typography></>}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Sua assinatura para documentos digitais (fundo transparente).</Typography>
                <input type="file" accept="image/png,image/jpeg" style={{ display: 'none' }} id="assinatura-upload" onChange={e => handleUpload(e, setAssinaturaUrl, 'assinaturaUrl')} />
                <label htmlFor="assinatura-upload"><Button component="span" variant="outlined" size="small" sx={{ borderColor: '#F97316', color: '#F97316', borderRadius: '8px', mr: 1 }}>Selecionar</Button></label>
                {assinaturaUrl && <Button size="small" color="error" onClick={() => { setAssinaturaUrl(''); localStorage.removeItem('assinaturaUrl'); }}>Remover</Button>}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline rows={2} label="Rodapé Padrão (Endereço, CRO Clínico, etc)" placeholder="Ex: Av. Paulista, 1000 - Conj 10 - São Paulo/SP - (11) 9999-9999" sx={fieldSx} />
          </Grid>
        </Grid>
      </Paper>

      {/* Modelos de Anamnese */}
      <Paper elevation={0} sx={cardSx}>
        {sectionTitle(AnamneseIcon, 'Modelos de Anamnese')}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Crie e gerencie questionários personalizados para seus pacientes.
        </Typography>
        {loadingAnamneses && <Typography variant="body2" color="text.secondary">Carregando modelos...</Typography>}
        {erroAnamneses && <Alert severity="error" sx={{ mb: 1 }}>{erroAnamneses}</Alert>}
        {!loadingAnamneses && !erroAnamneses && anamneses.length === 0 && (
          <Typography variant="body2" color="text.disabled" sx={{ mb: 1 }}>Nenhum modelo cadastrado ainda.</Typography>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          {anamneses.map(anamnese => (
            <Box key={anamnese.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '12px 16px', borderRadius: '10px', bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#FFF7ED', border: '1px solid rgba(249,115,22,0.15)' }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>{anamnese.titulo}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Última alteração: {anamnese.updatedAt ? new Date(anamnese.updatedAt).toLocaleDateString('pt-BR') : '-'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" startIcon={<EditIcon />} onClick={() => abrirModalAnamnese(anamnese)} sx={{ borderColor: '#F97316', color: '#F97316', borderRadius: '8px' }} variant="outlined">Editar</Button>
                <Button size="small" startIcon={<DeleteIcon />} onClick={() => setAnamneseExcluir(anamnese)} color="error" variant="outlined" sx={{ borderRadius: '8px' }}>Excluir</Button>
              </Box>
            </Box>
          ))}
        </Box>
        <Button fullWidth variant="outlined" startIcon={<AddIcon />} onClick={() => abrirModalAnamnese(null)}
          sx={{ borderStyle: 'dashed', borderColor: 'rgba(249,115,22,0.4)', color: '#F97316', borderRadius: '10px', py: 1.5, fontWeight: 700, '&:hover': { bgcolor: 'rgba(249,115,22,0.05)', borderColor: '#F97316' } }}>
          Criar Novo Modelo de Anamnese
        </Button>

        {/* Modal criar/editar anamnese */}
        <Dialog open={modalAberto} onClose={() => setModalAberto(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {anamneseEditando ? 'Editar Anamnese' : 'Nova Anamnese'}
            <IconButton size="small" onClick={() => setModalAberto(false)}><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField fullWidth size="small" label="Título" value={editTitulo} onChange={e => setEditTitulo(e.target.value)} required />
            <TextField fullWidth size="small" label="Conteúdo" multiline rows={10} value={editConteudo} onChange={e => setEditConteudo(e.target.value)} placeholder="Escreva as perguntas e instruções da anamnese. Use {{PACIENTE_NOME}}, {{DATA}} para variáveis." helperText="Dica: use {{PACIENTE_NOME}}, {{PACIENTE_CPF}}, {{DATA}}" />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button onClick={() => setModalAberto(false)} size="small">Cancelar</Button>
            <Button onClick={handleSalvarAnamnese} variant="contained" size="small" disabled={salvando || !editTitulo.trim()} sx={{ bgcolor: '#F97316', '&:hover': { bgcolor: '#ea6c0a' } }}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogActions>
        </Dialog>

        <ModalConfirmar open={!!anamneseExcluir} onClose={() => setAnamneseExcluir(null)} onConfirm={handleExcluirAnamnese} texto={`Deseja realmente excluir o modelo "${anamneseExcluir?.titulo || ''}"? Essa ação não pode ser desfeita.`} />
      </Paper>

      {/* Dados da Clínica */}
      <Paper elevation={0} sx={cardSx}>
        {sectionTitle(ClinicaIcon, 'Dados da Clínica')}
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Nome Fantasia da Clínica" value={clinicaNome} onChange={e => setClinicaNome(e.target.value)} disabled={clinicaLoading} sx={fieldSx} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="E-mail Comercial *" type="email" value={clinicaEmail} onChange={e => setClinicaEmail(e.target.value)} disabled={clinicaLoading} sx={fieldSx} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="WhatsApp / Telefone" value={clinicaTelefone} onChange={e => setClinicaTelefone(e.target.value)} disabled={clinicaLoading} sx={fieldSx} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="CNPJ" value={clinicaCnpj} onChange={e => setClinicaCnpj(e.target.value)} disabled={clinicaLoading} sx={fieldSx} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Tipo de Pessoa" value={clinicaTipoPessoa} disabled sx={fieldSx} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, borderColor: 'rgba(249,115,22,0.12)' }} />

        {sectionTitle(FaceIcon, 'Especialidades Atendidas')}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Controla quais seções aparecem na ficha do paciente (Odontograma e/ou Mapa Estético). Pelo menos uma precisa ficar ativa.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 1 }}>
          <FormControlLabel
            control={<Switch checked={atendeOdontologia} onChange={e => handleToggleOdontologia(e.target.checked)} disabled={clinicaLoading} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#F97316' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#F97316' } }} />}
            label="Odontologia"
          />
          <FormControlLabel
            control={<Switch checked={atendeEstetica} onChange={e => handleToggleEstetica(e.target.checked)} disabled={clinicaLoading} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#F97316' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#F97316' } }} />}
            label="Estética"
          />
        </Box>

        <Divider sx={{ my: 3, borderColor: 'rgba(249,115,22,0.12)' }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: '#ef4444', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DeleteIcon fontSize="small" /> Excluir conta da clínica
          </Typography>
          <Button variant="contained" startIcon={<SaveIcon />} disabled={clinicaLoading} onClick={handleSalvarTudo}
            sx={{ bgcolor: '#F97316', '&:hover': { bgcolor: '#EA580C' }, boxShadow: '0 2px 10px rgba(249,115,22,0.35)', borderRadius: '8px', fontWeight: 700 }}>
            Salvar Todas as Alterações
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Configuracao;
